// @ts-strict-ignore
import keyBy from 'lodash/keyBy';
import memoize from 'lodash/memoize';
import partition from 'lodash/partition';

import {qapiPropertyDefinitionsClient} from 'common/report/queries/query-api/property-definitions-client';
import {PropertyDefinition} from 'common/util/definitions-types';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {DataGroup} from 'common/widgets/data-group-selector/types';

import {ResourceType, SPECIAL_ALL_EVENT_VALUES} from '../constants';
import {reporter} from '../error-reporting';
import {Store} from './store';

export interface PropertyDefinitionMap {
  [propertyName: string]: PropertyDefinition;
}

export const transform = {
  index: (definitions: PropertyDefinition[]) => keyBy(definitions, `name`),
  update: (definitions) => ({k: definitions.name, v: definitions}),
};

const client = qapiPropertyDefinitionsClient;

/**
 * @deprecated
 * Deprecated store for Global event property definitions.
 * use {@link propertyDefinitionsForEventsStore} with no event parameter.
 */
export const eventPropertyDefinitionsStore = new Store<PropertyDefinitionMap, PropertyDefinition[]>({
  apiClient: client,
  defaultValue: {},
  name: `event-property-definitions`,
  transform,
});

/**
 * @deprecated
 */
export type EventPropertyDefinitionsStore = typeof eventPropertyDefinitionsStore;

export const getAllGlobalPropertyDefinitions = memoize(() => Object.values(eventPropertyDefinitionsStore.value));
eventPropertyDefinitionsStore.subscribe(() => {
  getAllGlobalPropertyDefinitions?.cache?.clear();
});

export function fetchGlobalEventPropertyDefinitions({
  callback = null,
  refresh = false,
}: {callback?: () => void; refresh?: boolean} = {}) {
  if (callback) {
    eventPropertyDefinitionsStore.subscribe(callback);
  }
  const params = {
    projectId: getProjectId(),
    workspaceId: getWorkspaceId(),
    category: ResourceType.Event,
  };

  if (refresh) {
    return eventPropertyDefinitionsStore.refresh(params);
  }
  return eventPropertyDefinitionsStore.fetch(params);
  // TODO: Migrate all global fetch calls through this util so we can flip over to event based store
  // return fetchPropertyDefinitionsForEvents([], {includeDimensionProperties, callback, refresh});
}

/**
 * Property Definitions for one, many, or all events
 *
 * We use a different store to manage definitions as they relate to any groups of events.
 * This is most commonly used by the query-builder to provide useful, contextual properties.
 * To query all event properties simple remove the event parameter. This method should be used over the now deprecated eventPropertyDefinitionsStore
 */
type StoreKeyDefinitionMap = {[paramsKey: string]: PropertyDefinitionMap};
export const propertyDefinitionsForEventsStore = new Store<StoreKeyDefinitionMap, PropertyDefinition[]>({
  apiClient: client,
  defaultValue: {},
  name: `event-properties-definitions`,

  /**
   * different properties-menu instances can have different properties they need to show
   * based on the event names given to them. here we maintain a map of fetch params to property lists
   */
  transform: {
    index: (response, {subscription}) => {
      const key = Store.getKey(subscription.params);
      const data = transform.index(response);
      return {[key]: data};
    },
  },
});

export function isQueryOnAllEvents(eventNames: string[]) {
  return (
    !eventNames ||
    eventNames.length === 0 ||
    eventNames.some((eventName) => SPECIAL_ALL_EVENT_VALUES.includes(eventName))
  );
}

export function eventDefinitionParamsAndKey(
  eventValues: string[],
  {
    includeDimensionProperties,
    projectId,
    workspaceId,
  }: {
    includeDimensionProperties: boolean;
    // projectId/workspaceId can be passed in as a perf optimization to avoid the parsing that's necessary to
    // extract these values from the url
    projectId?: string;
    workspaceId?: string;
  },
) {
  const [events, falsyEventValues] = partition<string>(eventValues, Boolean);
  if (falsyEventValues.length) {
    reporter.warn(`Invalid event name provided`);
  }
  const params = {
    event: events,
    projectId: projectId ?? getProjectId(),
    workspaceId: workspaceId ?? getWorkspaceId(),
    includeHidden: true,
    includeDensity: true,
    includeDimensionProperties,
    category: ResourceType.Event,
  };
  if (!events || isQueryOnAllEvents(events)) {
    delete params.event;
  }
  return {params, key: Store.getKey(params)};
}

export function getAllPropertyDefinitionsForEvents(
  events: string[],
  {includeDimensionProperties = false},
): PropertyDefinition[] {
  const {key} = eventDefinitionParamsAndKey(events, {includeDimensionProperties});
  return Object.values(propertyDefinitionsForEventsStore.value[key] || {});
}

export function fetchPropertyDefinitionsForEvents(
  events: string[],
  {includeDimensionProperties = false, refresh = false}: {includeDimensionProperties?: boolean; refresh?: boolean},
) {
  const {params, key} = eventDefinitionParamsAndKey(events, {includeDimensionProperties});
  if (refresh) {
    return propertyDefinitionsForEventsStore.refresh(params, {}, key);
  }
  return propertyDefinitionsForEventsStore.fetch(params, {}, key);
}

/**
 * Global property definitions for User Profiles
 */

export const peoplePropertyDefinitionsStore = new Store<PropertyDefinitionMap, PropertyDefinition[]>({
  apiClient: client,
  defaultValue: {},
  name: `people-property-definitions`,
  transform,
});

export type PeoplePropertyDefinitionsStore = typeof peoplePropertyDefinitionsStore;

function userDefinitionParamsAndKey({includeCustom = false}: {includeCustom?: boolean}) {
  const params = {
    projectId: getProjectId(),
    workspaceId: getWorkspaceId(),
    category: ResourceType.User,
    includeCustom,
  };
  return {params, key: Store.getKey(params)};
}

export async function fetchAndGetAllPropertyDefinitionsForUserProfiles({
  includeCustom = false,
  refresh = false,
}: {
  includeCustom?: boolean;
  refresh?: boolean;
} = {}) {
  await fetchPropertyDefinitionsForUserProfiles({includeCustom, refresh});
  return getAllPropertyDefinitionsForUserProfiles();
}

export function getAllPropertyDefinitionsForUserProfiles(): PropertyDefinition[] {
  return Object.values(peoplePropertyDefinitionsStore?.value || {});
}

export function fetchPropertyDefinitionsForUserProfiles({
  refresh = false,
  includeCustom = false,
  callback = null,
}: {
  refresh?: boolean;
  includeCustom?: boolean;
  callback?: () => void;
}) {
  const {params} = userDefinitionParamsAndKey({includeCustom});
  if (callback) {
    peoplePropertyDefinitionsStore.subscribe(callback);
  }

  if (refresh) {
    return peoplePropertyDefinitionsStore.refresh(params);
  }

  return peoplePropertyDefinitionsStore.fetch(params);
}

/**
 * Global property definitions for Group Profiles
 */

export const groupPropertyDefinitionsStore = new Store<PropertyDefinitionMap, PropertyDefinition[]>({
  apiClient: client,
  defaultValue: {},
  name: `people-property-definitions`,
  transform,
});

export function getAllPropertyDefinitionsForGroupProfiles(): PropertyDefinition[] {
  return Object.values(groupPropertyDefinitionsStore?.value || {});
}

// Fetch and update the groupPropertiesStore
export async function fetchAllPropertyDefinitionsForGroupProfiles(dataGroups: Obj<DataGroup>) {
  const storeObject: PropertyDefinitionMap = {};
  const dataGroupIds = dataGroups ? Object.keys(dataGroups) : [];

  // Not using the store's own fetch because we want to do some intermediary processing since there are naming conflicts between properties across data groups
  const promises = await Promise.allSettled<TODO[]>(
    dataGroupIds.map((dataGroupId) => {
      const promise = client.query({
        dataGroupId,
        workspaceId: getWorkspaceId(),
        projectId: getProjectId(),
        category: ResourceType.User,
      });

      return promise;
    }),
  );

  const properties = [];
  // With allSettled, even if some promises fail we can still process the successful ones
  for (const promise of promises) {
    if (promise.status === `fulfilled`) {
      properties.push(...promise.value);
    } else {
      reporter.error(`Error fetching group property definitions`, promise.reason);
    }
  }
  properties.forEach((groupProperty) => {
    storeObject[`${groupProperty.name}-${groupProperty.dataGroupId}`] = groupProperty;
  });

  groupPropertyDefinitionsStore.update(storeObject);
}
